import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { PageShell, Text } from "@/components/ui";
import { CoursePlayer } from "@/components/course-player";
import { useAuthStore } from "@/stores/authStore";
import { fetchLessonContent, fetchPlayerOutline } from "@/services/api/player";

export function TenantLearnLessonPage() {
  const {
    tenantSlug = "",
    courseSlug = "",
    lessonId = "",
  } = useParams<{
    tenantSlug: string;
    courseSlug: string;
    lessonId: string;
  }>();
  const t = useTranslations("nav");
  const tPlayer = useTranslations("player");
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const outlineQuery = useQuery({
    queryKey: ["player-outline", courseSlug],
    queryFn: () => fetchPlayerOutline(courseSlug),
    enabled: isAuthenticated && Boolean(courseSlug),
    retry: false,
  });

  const activeLesson = outlineQuery.data?.modules
    .flatMap((mod) => mod.lessons)
    .find((lesson) => lesson.id === lessonId);

  const contentQuery = useQuery({
    queryKey: ["lesson-content", lessonId],
    queryFn: () => fetchLessonContent(lessonId),
    enabled:
      isAuthenticated && Boolean(lessonId) && Boolean(activeLesson && !activeLesson.isLocked),
    retry: false,
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (outlineQuery.isLoading) {
    return (
      <PageShell data-testid="course-player-loading">
        <Text tone="muted">{tPlayer("loading")}</Text>
      </PageShell>
    );
  }

  if (outlineQuery.isError || !outlineQuery.data) {
    const status = (outlineQuery.error as { response?: { status?: number } }).response?.status;
    if (status === 403) {
      return <Navigate to={`/t/${tenantSlug}/courses/${courseSlug}`} replace />;
    }
    return (
      <PageShell data-testid="course-player-not-found">
        <Text tone="muted">
          {status === 404 ? tPlayer("courseNotFound") : tPlayer("courseLoadFailed")}
        </Text>
      </PageShell>
    );
  }

  const lessonExists = outlineQuery.data.modules.some((mod) =>
    mod.lessons.some((lesson) => lesson.id === lessonId),
  );
  if (!lessonExists) {
    return (
      <PageShell data-testid="course-player-lesson-not-found">
        <Text tone="muted">{tPlayer("lessonNotFound")}</Text>
      </PageShell>
    );
  }

  if (
    activeLesson &&
    !activeLesson.isLocked &&
    (contentQuery.isLoading || (contentQuery.isFetching && !contentQuery.data))
  ) {
    return (
      <PageShell data-testid="course-player-content-loading">
        <Text tone="muted">{tPlayer("loadingLesson")}</Text>
      </PageShell>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
      <Link
        to={`/t/${tenantSlug}/dashboard`}
        className="text-sm text-[var(--text-muted)] hover:underline"
      >
        {t("backToDashboard")}
      </Link>
      <div className="mt-4">
        <CoursePlayer
          tenantSlug={tenantSlug}
          courseSlug={courseSlug}
          courseId={outlineQuery.data.course.id}
          courseTitle={outlineQuery.data.course.title}
          activeLessonId={lessonId}
          modules={outlineQuery.data.modules}
          lessonContent={contentQuery.data ?? null}
          cohortName={outlineQuery.data.cohortName ?? null}
        />
      </div>
    </main>
  );
}
